import { Alert, Box, CircularProgress, Divider, Typography } from '@mui/material';
import { DrawerContainer } from '../../../../components/Drawer/DrawerContainer';
import { DrawerHeader } from '../../../../components/Drawer/DrawerHeader';
import { DatosSiniestroApi } from '../../consts/types';
import { formatNombreConCuit } from '../../../Siniestros/utils/formatNombreConCuit';
import styles from './SobreSiniestroDrawer.module.css';

type Props = {
    abierto: boolean;
    onCerrar: () => void;
    datos: DatosSiniestroApi | null;
    cargando: boolean;
    error: string | null;
    // El Preventor ve Contrato y Organismo en lugar del empleador de la denuncia (SMP-484).
    esPreventor?: boolean;
};

const Campo = ({ label, value }: { label: string; value?: string | number | null }) => (
    <Box className={styles.campo}>
        <Typography variant="caption" className={styles.campoLabel}>
            {label}
        </Typography>
        <Typography variant="body2" className={styles.campoValor}>
            {value ?? '—'}
        </Typography>
    </Box>
);

const Seccion = ({ titulo, children }: { titulo: string; children: React.ReactNode }) => (
    <Box className={styles.seccion}>
        <Typography variant="subtitle2" className={styles.seccionTitulo}>
            {titulo}
        </Typography>
        <Divider sx={{ mb: 1.5 }} />
        <Box className={styles.seccionCampos}>{children}</Box>
    </Box>
);

const formatearFecha = (fecha?: string | null): string | null => {
    if (!fecha) return null;
    try {
        return new Date(fecha).toLocaleDateString('es-AR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    } catch {
        return fecha;
    }
};

const formatearDireccion = (datos: DatosSiniestroApi['lugarOcurrencia']) => {
    if (!datos) return null;
    const partes = [
        datos.calle && datos.altura ? `${datos.calle} ${datos.altura}` : datos.calle,
        datos.piso ? `Piso ${datos.piso}` : null,
        datos.depto ? `Depto ${datos.depto}` : null,
        datos.localidad?.nombre,
        datos.localidad?.provincia?.nombre,
        datos.codigoPostal ? `(${datos.codigoPostal})` : null
    ].filter(Boolean);
    return partes.length ? partes.join(', ') : null;
};

const tieneAlgunValor = (...valores: (string | number | null | undefined)[]) =>
    valores.some((v) => v != null && v !== '');

const SobreSiniestroDrawer = ({
    abierto,
    onCerrar,
    datos,
    cargando,
    error,
    esPreventor = false
}: Props) => {
    const lugarOcurrenciaDir = datos ? formatearDireccion(datos.lugarOcurrencia) : null;
    // '' (sin nombre ni CUIT) pasa a null para que Campo muestre "—".
    const contrato = formatNombreConCuit(datos?.contrato?.nombre, datos?.contrato?.cuit) || null;
    const organismo = formatNombreConCuit(datos?.organismo?.nombre, datos?.organismo?.cuit) || null;
    // El Preventor no ve el empleador de la denuncia (el cliente / A.R.T.) sino el contrato y
    // el organismo del siniestro; el resto de los roles, como hasta ahora.
    const datosEmpresa = esPreventor
        ? [
              { label: 'Contrato', value: contrato },
              { label: 'Organismo', value: organismo }
          ]
        : [
              { label: 'Empleador', value: datos?.empleadorRazonSocial },
              { label: 'CUIT empleador', value: datos?.cuitEmpleador }
          ];

    const mostrarLugarOcurrencia = tieneAlgunValor(lugarOcurrenciaDir);
    const mostrarDenuncia = tieneAlgunValor(
        datos?.numeroDenuncia,
        ...datosEmpresa.map((campo) => campo.value),
        datos?.fechaOcurrencia,
        datos?.idTipoDenuncia,
        datos?.fechaRecepcion,
        datos?.fechaDenuncia
    );
    const mostrarPaciente = tieneAlgunValor(
        datos?.documento,
        datos?.cuil,
        datos?.nombre,
        datos?.apellido,
        datos?.fechaNacimiento,
        datos?.estadoCivil,
        datos?.domicilio,
        datos?.telefonoPrincipal,
        datos?.telefonoCelular,
        datos?.telefonoSecundario
    );
    const mostrarLugarTrabajo = tieneAlgunValor(
        datos?.nombreEstablecimiento,
        datos?.direccionTrabajo,
        datos?.localidadTrabajo,
        datos?.provinciaTrabajo,
        datos?.ocupacion,
        datos?.fechaIngreso,
        datos?.horarioLaboral
    );
    const mostrarMedicos = tieneAlgunValor(
        datos?.estadoMedico,
        datos?.cie10,
        datos?.diagnosticoCerteza
    );
    const mostrarRelato = tieneAlgunValor(datos?.relatoHecho);

    return (
        <DrawerContainer open={abierto} setOpen={() => onCerrar()} onClose={onCerrar}>
            <DrawerHeader onIconClick={onCerrar} text="Sobre el siniestro" />

            {cargando && (
                <Box className={styles.centrado}>
                    <CircularProgress />
                </Box>
            )}

            {error && !cargando && (
                <Alert severity="error" sx={{ mt: 2 }}>
                    {error}
                </Alert>
            )}

            {datos && !cargando && !error && (
                <Box className={styles.contenido}>
                    {mostrarLugarOcurrencia && (
                        <Seccion titulo="Lugar de ocurrencia">
                            <Campo label="Dirección" value={lugarOcurrenciaDir} />
                        </Seccion>
                    )}

                    {mostrarDenuncia && (
                        <Seccion titulo="Datos de la denuncia">
                            <Campo label="Número de denuncia" value={datos.numeroDenuncia} />
                            {datosEmpresa.map((campo) => (
                                <Campo key={campo.label} label={campo.label} value={campo.value} />
                            ))}
                            <Campo label="Fecha de ocurrencia" value={formatearFecha(datos.fechaOcurrencia)} />
                            <Campo label="Tipo de denuncia" value={datos.idTipoDenuncia} />
                            <Campo label="Fecha de recepción" value={formatearFecha(datos.fechaRecepcion)} />
                            <Campo label="Fecha de denuncia" value={formatearFecha(datos.fechaDenuncia)} />
                        </Seccion>
                    )}

                    {mostrarPaciente && (
                        <Seccion titulo="Datos del paciente">
                            <Campo label="Documento" value={datos.documento} />
                            <Campo label="CUIL" value={datos.cuil} />
                            <Campo
                                label="Nombre completo"
                                value={
                                    tieneAlgunValor(datos.nombre, datos.apellido)
                                        ? `${datos.nombre ?? ''} ${datos.apellido ?? ''}`.trim()
                                        : null
                                }
                            />
                            <Campo label="Fecha de nacimiento" value={formatearFecha(datos.fechaNacimiento)} />
                            <Campo label="Estado civil" value={datos.estadoCivil} />
                            <Campo label="Domicilio" value={datos.domicilio} />
                            <Campo label="Teléfono principal" value={datos.telefonoPrincipal} />
                            <Campo label="Teléfono celular" value={datos.telefonoCelular} />
                            <Campo label="Teléfono secundario" value={datos.telefonoSecundario} />
                        </Seccion>
                    )}

                    {mostrarLugarTrabajo && (
                        <Seccion titulo="Datos del lugar de trabajo">
                            <Campo label="Establecimiento" value={datos.nombreEstablecimiento} />
                            <Campo label="Dirección" value={datos.direccionTrabajo} />
                            <Campo label="Localidad" value={datos.localidadTrabajo} />
                            <Campo label="Provincia" value={datos.provinciaTrabajo} />
                            <Campo label="Ocupación" value={datos.ocupacion} />
                            <Campo label="Fecha de ingreso" value={formatearFecha(datos.fechaIngreso)} />
                            <Campo label="Horario laboral" value={datos.horarioLaboral} />
                        </Seccion>
                    )}

                    {mostrarMedicos && (
                        <Seccion titulo="Datos médicos">
                            <Campo label="Estado médico" value={datos.estadoMedico} />
                            <Campo label="CIE 10" value={datos.cie10} />
                            <Campo label="Diagnóstico de certeza" value={datos.diagnosticoCerteza} />
                        </Seccion>
                    )}

                    {mostrarRelato && (
                        <Seccion titulo="Relato">
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                {datos.relatoHecho}
                            </Typography>
                        </Seccion>
                    )}
                </Box>
            )}
        </DrawerContainer>
    );
};

export default SobreSiniestroDrawer;
